import { P } from 'components'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingV2 from 'components/LoadingV2'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import { TableComponent as DataTable } from 'components/TableComponent'
import moment from 'moment-timezone'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Download, Edit2 } from 'react-feather'
import { useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { handleSummaryDownloadExcel } from '../downloadReport'
import ModalSetTilte from './ModalSetTilte'
import {
  useAddReportMutation,
  useFetchConfigurationQuery,
  useFetchReportsQuery,
} from './reportPnbpListApiSlice'
import './styles.css'

const DetailCombinedContent = () => {
  const { data: reports, refetch, isFetching } = useFetchReportsQuery()
  const { data, isLoading } = useFetchConfigurationQuery()
  const { enqueueSnackbar } = useSnackbar()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [editDataItemTitle, setEditDataItemTitle] = useState(null)
  const isEditModeTitle = Boolean(editDataItemTitle)

  // DOWNLOAD
  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [addReport, { isLoading: isAdding }] = useAddReportMutation()

  const [isOpenModalSetTitle, setIsOpenModalSetTitle] = useState(false)
  const toggleModalSetTitle = () => {
    setIsOpenModalSetTitle((prev) => !prev)
    setEditDataItemTitle(null)
  }

  const findReport = reports?.find((report) => report?.id === id)

  const [payload, setPayload] = useState({
    id: id,
    title: '',
    periode: '',
    status: '',
    itemConfigurations: [
      {
        type: null,
        commodity: null,
        psdh: null,
        unit: null,
        volume: null,
        total: null,
      },
    ],
  })

  //   HANDLE TITLE
  const handleTitleChange = (title) => {
    setPayload((prev) => ({
      ...prev,
      title,
    }))
  }

  //   HANDLE PERIODE
  const handlePeriodeChange = (periode) => {
    setPayload((prev) => ({
      ...prev,
      periode,
    }))
  }

  //   RESET STATE
  const resetState = () => {
    setPayload({
      id: null,
      title: '',
      periode: '',
      status: 'Draft',
      itemConfigurations: [
        {
          type: null,
          commodity: null,
          psdh: null,
          unit: null,
          volume: null,
          total: null,
        },
      ],
    })
  }

  // HANDLE SUBMIT TITLE
  const handleSubmitTitle = async () => {
    try {
      if (!editDataItemTitle) {
        enqueueSnackbar('Lengkapi data terlebih dahulu', {
          variant: 'error',
        })
        return
      }

      const payloadArray = (reports || []).map((report) => {
        if (report.id === id) {
          return { ...payload, id: id }
        }
        return report
      })

      const response = await addReport(payloadArray)
      if (response && response?.data) {
        enqueueSnackbar('Laporan berhasil diubah', {
          variant: 'success',
        })
        refetch()
        toggleModalSetTitle()
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Laporan gagal diubah', {
        variant: 'error',
      })
    }
  }

  // CALCULATE GRAND TOTAL
  const calculateGrandTotal = () => {
    return (findReport?.itemConfigurations || []).reduce((sum, item) => {
      const total = item?.total || 0
      return sum + total
    }, 0)
  }

  // GROUPED DATA BY TYPE AND SUM VOLUMES
  const groupDataByType = (data) => {
    if (!data) return []

    return data.reduce((acc, item) => {
      const type = item.type.value
      const existingGroup = acc.find((group) => group.type.value === type)

      if (existingGroup) {
        existingGroup.psdh += item.psdh
        existingGroup.volume += item.volume
        existingGroup.total += item.total
        existingGroup.items.push(item)
        existingGroup.itemCount = existingGroup.items.length
      } else {
        acc.push({
          type: item.type,
          psdh: item.psdh,
          unit: item.unit,
          volume: item.volume,
          total: item.total,
          items: [item],
          isGroup: true,
          itemCount: 1,
        })
      }
      return acc
    }, [])
  }

  // HANDLE GROUPED DATA
  const groupedData = groupDataByType(findReport?.itemConfigurations)
  const multiItemGroups = groupedData.filter((group) => group.itemCount > 1)

  // HANDLE COLUMNS COMBINED
  const columns = [
    {
      name: <span>Jenis</span>,
      selector: 'type',
      grow: 2,
      minWidth: '150px',
      cell: (row) => {
        return <span>{row?.type?.label ?? '-'}</span>
      },
    },
    // HIDE KOMODITAS FOR COMBINED
    {
      name: <span>Barang</span>,
      selector: 'commodity',
      grow: 1,
      left: true,
      minWidth: '200px',
      cell: (row) => {
        return (
          <ul style={{ padding: 4, margin: 0 }}>
            {row.isGroup
              ? row?.items?.map((item, i) => (
                  <li key={i}>{item?.commodity?.label ?? '-'}</li>
                ))
              : (row?.commodity?.label ?? '-')}
          </ul>
        )
      },
    },
    {
      name: <span>Volume</span>,
      selector: 'volume',
      right: true,
      minWidth: '90px',
      maxWidth: '90px',
      cell: (row) => {
        return (
          <span>
            {row.isGroup ? formatInputNumber(row.volume) : (row?.volume ?? '-')}
          </span>
        )
      },
    },
    {
      name: <span>Satuan</span>,
      selector: 'unit',
      minWidth: '90px',
      maxWidth: '90px',
      left: true,
      cell: (row) => {
        return <span>{row?.unit?.label ?? '-'}</span>
      },
    },
    {
      name: <span>PSDH</span>,
      selector: 'psdh',
      right: true,
      cell: (row) => {
        return <span>Rp{formatInputNumber(row?.psdh ?? 0)}</span>
      },
    },
    {
      name: <span>Kewajiban Bayar</span>,
      selector: 'total',
      grow: 1,
      right: true,
      minWidth: '120px',
      cell: (row) => {
        return <span>Rp{formatInputNumber(row?.total ?? 0)}</span>
      },
    },
  ]

  // HANDLE IS FORM INVALID TITLE
  const isFormInvalidTitle = !payload.title || !payload.periode

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <>
      {/* MODAL SET TITLE */}
      <ModalSetTilte
        isOpen={isOpenModalSetTitle}
        toggle={toggleModalSetTitle}
        payload={payload}
        handleTitleChange={handleTitleChange}
        handlePeriodeChange={handlePeriodeChange}
        resetState={resetState}
        isFormInvalid={isFormInvalidTitle}
        handleSubmit={handleSubmitTitle}
        isLoadingAddReport={isAdding}
        isEditMode={isEditModeTitle}
      />

      {/* PROGRESS DOWNLOAD */}
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />

      {/* COMBINED BASED ON TYPE */}
      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            columns={columns}
            data={groupedData ?? []}
            progressPending={isFetching || isLoading}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner color="primary" />
              </div>
            }
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center w-100">
                  <div className="d-flex flex-column mb-3 mb-md-0">
                    <P size="regular" className="fw-semibold">
                      {findReport?.title}
                    </P>
                    <P size="small" className="fw-semibold">
                      {findReport?.periode
                        ? moment(findReport?.periode).format('MMMM YYYY')
                        : '-'}
                    </P>
                  </div>

                  <div className="btn-scroll-wrapper d-md-flex gap-2">
                    <div className="d-flex flex-nowrap overflow-auto gap-2">
                      <CustomButton
                        disabled={isDownload || !groupedData?.length}
                        className="rounded flex-grow-1 text-center mb-2 mb-md-0"
                        testId="pnbp-download-button"
                        onClick={() =>
                          handleSummaryDownloadExcel(
                            `${findReport?.title}_${findReport?.periode}`,
                            groupedData,
                            setIsDownload,
                            setFirstRows,
                            setTotalRows,
                            calculateGrandTotal(),
                          )
                        }
                        color="primary"
                        outline
                        size="sm"
                      >
                        {isDownload ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          <Download size={14} />
                        )}{' '}
                        Download
                      </CustomButton>

                      <Button
                        onClick={() => {
                          toggleModalSetTitle()
                          setEditDataItemTitle(findReport)
                          setPayload(findReport)
                        }}
                        size="sm"
                        color="primary"
                        outline
                        className="flex-shrink-0"
                      >
                        <Edit2 size={14} /> Ubah Judul
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                Belum ada detail laporan pnbp yang tersedia
              </p>
            }
          />

          <div
            style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #dee2e6',
              minHeight: '48px',
              borderTop: 'none',
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <div
              className="d-flex align-items-center gap-2"
              style={{ marginRight: '12px' }}
            >
              <P
                size={isMobile ? 'regular' : 'default'}
                className="fw-semibold "
              >
                Total Bayar:{' '}
              </P>
              <P bold className="text-primary">
                Rp{formatInputNumber(calculateGrandTotal())}
              </P>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default DetailCombinedContent
